import { observer } from 'mobx-react';
import { t } from 'i18next';
import { Button, Flex, ScrollArea, Table, Stack } from '@mantine/core';
import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import SurveyCalculatedScoringRow from './SurveyCalculatedScoringRow.tsx';
import { ICategoryConfig } from '../../../global/entities/Questionnaires/Questionnaires.ts';
import { Th, useTable } from '../../../global/hooks/useTable.tsx';

export interface ISurveyCalculatedScoringProps {
    configs: ICategoryConfig[];
    onChangesConfigs: Dispatch<SetStateAction<ICategoryConfig[]>>;
    showErrors: boolean;
    rawJson?: string;
}

let nextNegativeId = -1;

const SurveyCalculatedScoring: React.FC<ISurveyCalculatedScoringProps> = observer(
    ({ configs, onChangesConfigs, showErrors, rawJson }) => {
        const { setSorting, sortBy, reverseSortDirection, setSortedData, sortedData } = useTable();

        // Recursively find all expression names (calculatedValues and expression elements)
        const findExpressionElements = (elements: any[]): string[] => {
            const names: string[] = [];
            for (const element of elements) {
                // Extract if it's a calculatedValue (no type) or expression element
                if (element.name && (!element.type || element.type === 'expression'))
                    names.push(element.name);

                // Check nested elements (panels, etc.)
                if (element.elements && Array.isArray(element.elements))
                    names.push(...findExpressionElements(element.elements));
            }
            return names;
        };

        // Extract calculatedValues from SurveyJS JSON and expression elements from pages
        const calculatedValueOptions = useMemo(() => {
            if (!rawJson) return [];

            try {
                const surveyJson = JSON.parse(rawJson);
                const names = new Set<string>();

                // Extract from calculatedValues (root level) and expression elements from pages
                const allSources = [
                    ...(surveyJson.calculatedValues || []),
                    ...(surveyJson.pages || []).flatMap((page: any) => page.elements || [])
                ];
                
                for (const name of findExpressionElements(allSources))
                    names.add(name);
                
                return Array.from(names).map((name) => ({
                    value: name,
                    label: name,
                }));
            } catch (error) {
                console.error('Failed to parse survey JSON for calculatedValues:', error);
            }

            return [];
        }, [rawJson]);

        useEffect(() => {
            setSortedData([]);
            if (configs?.length) 
                setSortedData(configs);
            
        }, [configs]);

        const handleAddRow = () => {
            const newRow: ICategoryConfig = {
                id: nextNegativeId--,
                categoryFr: '',
                categoryEn: '',
                calculatedValueName: '',
            };
            onChangesConfigs((prev) => [...prev, newRow]);
        };

        return (
            <Stack gap="md">
                <ScrollArea>
                    <Table verticalSpacing="xs">
                        <Table.Thead>
                            <Table.Tr>
                                <Th
                                    sorted={sortBy === 'categoryFr'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('categoryFr', sortedData)}
                                >
                                    {t('table.survey.categoryFr')}
                                </Th>

                                <Th
                                    sorted={sortBy === 'categoryEn'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('categoryEn', sortedData)}
                                >
                                    {t('table.survey.categoryEn')}
                                </Th>
                                <Th
                                    sorted={sortBy === 'calculatedValueName'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('calculatedValueName', sortedData)}
                                >
                                    {t('formSurvey.calculatedValueName')}
                                </Th>
                                <Table.Th></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {sortedData?.map((config: ICategoryConfig, index: number) => {
                                // Filter options: show only values not selected by OTHER rows
                                const selectedValues = configs
                                    .filter((_, idx) => idx !== index)
                                    .map(c => c.calculatedValueName)
                                    .filter(name => name); // Remove empty values
                                
                                const availableOptions = calculatedValueOptions.filter(
                                    (option: { value: string; label: string }) => !selectedValues.includes(option.value)
                                );

                                return (
                                    <SurveyCalculatedScoringRow
                                        key={index}
                                        config={config}
                                        index={index}
                                        onChangesConfigs={onChangesConfigs}
                                        calculatedValueOptions={availableOptions}
                                        showErrors={showErrors}
                                    />
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>

                <Flex justify="flex-end" mt="md">
                    <Button m={'sm'} onClick={handleAddRow}>
                        {t('button.add')}
                    </Button>
                </Flex>
            </Stack>
        );
    }
);

export default SurveyCalculatedScoring;
